import { useEffect, useState, useMemo } from "react";
import { Table, Spinner, Badge, Button, Row, Col, Form } from "react-bootstrap";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import api from "../../../axiosConfig";

export default function StockToolsMinimumPage() {
  const [allTools, setAllTools] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMinimumTools();
  }, []);

  const fetchMinimumTools = async () => {
    try {
      setLoading(true);
      const res = await api.get("/stock-tools");
      setAllTools(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Ambil kategori unik otomatis
  const categories = useMemo(() => {
    return [...new Set(allTools.map((item) => item.category).filter(Boolean))];
  }, [allTools]);

  // ✅ Filter tools (minimum + kategori)
  const filteredTools = useMemo(() => {
    let data = allTools.filter((item) => Number(item.stock_qty) <= 2);

    if (selectedCategory !== "all") {
      data = data.filter((item) => item.category === selectedCategory);
    }

    return data;
  }, [allTools, selectedCategory]);

  // ✅ EXPORT EXCEL (rapih + auto width + tanggal)
  const handleDownloadExcel = () => {
    const formattedData = filteredTools.map((item, index) => ({
      No: index + 1,
      Kategori: item.category || "-",
      Kode: item.kode,
      Nama: item.part_name,
      Spec: item.spec || "-",
      Stock: item.stock_qty,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Auto width kolom
    const colWidths = Object.keys(formattedData[0] || {}).map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map((row) =>
            row[key] ? row[key].toString().length : 0,
          ),
        ) + 2,
    }));

    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Minimum");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const today = new Date().toISOString().split("T")[0];

    saveAs(blob, `Stock_Minimum_Tools_${today}.xlsx`);
  };

  return (
    <div
      className="d-flex flex-column"
      style={{ height: "calc(103vh - 140px)" }}
    >
      {/* HEADER */}
      <Row className="mb-3 align-items-center">
        <Col md={6}>
          <h4 style={{ fontSize: "30px" }}>⚠️ STOCK MINIMUM TOOLS</h4>
        </Col>

        <Col md={6} className="d-flex justify-content-end gap-2">
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ maxWidth: "250px" }}
          >
            <option value="all">Semua Kategori</option>
            {categories.map((cat, index) => (
              <option key={index} value={cat}>
                {cat}
              </option>
            ))}
          </Form.Select>

          <Button
            variant="success"
            onClick={handleDownloadExcel}
            disabled={filteredTools.length === 0}
          >
            Export Excel
          </Button>
        </Col>
      </Row>

      {/* TABLE */}
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            border: "1px solid #dee2e6",
            borderRadius: "6px",
          }}
        >
          <Table striped bordered hover className="mb-0">
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 2,
              }}
            >
              <tr>
                <th className="text-center">No</th>
                <th>Kategori</th>
                <th>Kode</th>
                <th>Nama</th>
                <th>Spec</th>
                <th>Stock</th>
              </tr>
            </thead>
            <tbody>
              {filteredTools.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                filteredTools.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-center">{i + 1}</td>
                    <td>{item.category || "-"}</td>
                    <td className="cell-left">{item.kode}</td>
                    <td className="cell-left">{item.part_name}</td>
                    <td className="cell-left">{item.spec || "-"}</td>
                    <td className="text-center">
                      <Badge bg="danger">{item.stock_qty}</Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
